import React, { useState } from 'react';
import { DownloadSimple, Info, Warning, WarningOctagon } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { PeopleTab } from './PeopleTab';
import { annex95Csv, downloadCsv, formatMoney, PURCHASE_FILTERS, PURCHASE_REASONS, purchasesCsv, salesCsv, sumBy } from './taxModel';

const SEVERITY = {
  error: { icon: WarningOctagon, cls: 'border-red-500/50 bg-red-500/10 text-red-100' },
  warning: { icon: Warning, cls: 'border-amber-500/50 bg-amber-500/10 text-amber-100' },
  info: { icon: Info, cls: 'border-sky-500/40 bg-sky-500/10 text-sky-100' },
};

export function WarningsPanel({ warnings }) {
  if (!warnings.length) return null;
  const order = { error: 0, warning: 1, info: 2 };
  const sorted = [...warnings].sort((a, b) => order[a.severity] - order[b.severity]);
  return (
    <ul className="no-print space-y-2" aria-label="Alertas del reporte" data-testid="tax-warnings">
      {sorted.map((warning) => {
        const { icon: Icon, cls } = SEVERITY[warning.severity] || SEVERITY.info;
        return (
          <li key={`${warning.code}-${warning.message}`} className={`flex gap-2 rounded-lg border px-3 py-2 text-sm ${cls}`}>
            <Icon size={18} weight="fill" className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{warning.message}</span>
          </li>
        );
      })}
    </ul>
  );
}

const cell = 'whitespace-nowrap px-3 py-2 text-sm text-[#D1D5DB]';
const head = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]';

function Table({ columns, rows, footer, empty }) {
  if (!rows.length) return <p className="rounded-lg border border-[#242E42] bg-[#151B26] p-6 text-center text-sm text-[#9CA3AF]">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-[#242E42]">
      <table className="w-full min-w-[720px] border-collapse">
        <thead className="bg-[#151B26]"><tr>{columns.map((c) => <th key={c} className={head}>{c}</th>)}</tr></thead>
        <tbody>{rows}</tbody>
        {footer && <tfoot className="bg-[#151B26] font-semibold">{footer}</tfoot>}
      </table>
    </div>
  );
}

const num = 'whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-[#F3F4F6]';

const BUYER_OPTIONS = [
  { status: 'ok', label: 'Sí', title: 'La factura trae el nombre o RUC de la empresa', on: 'bg-emerald-500/25 text-emerald-300 border-emerald-500/60' },
  { status: 'missing', label: 'No', title: 'La factura NO trae el nombre ni el RUC de la empresa: sin crédito fiscal', on: 'bg-red-500/25 text-red-300 border-red-500/60' },
  { status: 'unknown', label: '?', title: 'Sin verificar', on: 'bg-[#242E42] text-[#D1D5DB] border-[#3A4560]' },
];

function BuyerMarker({ row, disabled, onChange }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-[#242E42]" role="group" aria-label={`Factura con nombre o RUC de la empresa: ${row.po_number}`}>
      {BUYER_OPTIONS.map((option) => {
        const active = row.buyer_check === option.status;
        return (
          <button
            key={option.status}
            type="button"
            title={disabled ? 'El período está presentado' : option.title}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => !active && onChange(row, option.status)}
            className={`h-7 w-8 border-l border-[#242E42] text-xs font-semibold first:border-l-0 disabled:cursor-not-allowed disabled:opacity-50 ${active ? option.on : 'text-[#6B7280] hover:bg-[#1E2636]'}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}


export function ReportDetail({ report, onBuyerCheck, onPayeeType }) {
  const { sales, purchases, retention_annex: annex, period } = report;
  const [tab, setTab] = useState('sales');
  const [filterId, setFilterId] = useState('all');
  const included = purchases.filter((row) => row.included);
  const activeFilter = PURCHASE_FILTERS.find((filter) => filter.id === filterId) || PURCHASE_FILTERS[0];
  const shown = purchases.filter(activeFilter.test);

  return (
    <Tabs value={tab} onValueChange={setTab} className="no-print">
      <TabsList className="flex h-auto flex-wrap bg-[#151B26]">
        <TabsTrigger value="sales">Ventas ({sales.length})</TabsTrigger>
        <TabsTrigger value="purchases">Compras ({purchases.length})</TabsTrigger>
        <TabsTrigger value="people">Ayudantes y dueño ({report.payments_to_people.reduce((n, g) => n + g.count, 0)})</TabsTrigger>
        <TabsTrigger value="annex">Anexo 95 retención ({annex.length})</TabsTrigger>
      </TabsList>

      <TabsContent value="sales" className="space-y-3">
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => downloadCsv(`itbms-ventas-${period}.csv`, salesCsv(sales))} className="border-[#242E42]"><DownloadSimple size={16} className="mr-1" /> Exportar CSV</Button>
        </div>
        <Table
          columns={['Factura', 'Fecha', 'Cliente', 'Base', 'Tasa', 'ITBMS pleno', 'ITBMS cobrado', 'Retenido']}
          empty="No hay facturas emitidas en este período."
          rows={sales.map((row) => (
            <tr key={row.invoice_number} className="border-t border-[#242E42]">
              <td className={cell}>{row.invoice_number}</td><td className={cell}>{row.issue_date}</td><td className={cell}>{row.client_name}</td>
              <td className={num}>{formatMoney(row.base)}</td><td className={num}>{row.rate}%</td><td className={num}>{formatMoney(row.tax_full)}</td>
              <td className={num}>{formatMoney(row.tax_collected)}</td><td className={num}>{formatMoney(row.tax_retained)}</td>
            </tr>
          ))}
          footer={(
            <tr><td className={cell} colSpan={3}>Total</td><td className={num}>{formatMoney(sumBy(sales, 'base'))}</td><td />
              <td className={num}>{formatMoney(sumBy(sales, 'tax_full'))}</td><td className={num}>{formatMoney(sumBy(sales, 'tax_collected'))}</td><td className={num}>{formatMoney(sumBy(sales, 'tax_retained'))}</td></tr>
          )}
        />
      </TabsContent>

      <TabsContent value="purchases" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-[#9CA3AF]">Solo las compras con ITBMS, factura de respaldo y el nombre o RUC de la empresa en la factura generan crédito fiscal. Las demás son gastos de la empresa sin crédito.</p>
          <Button size="sm" variant="outline" onClick={() => downloadCsv(`itbms-compras-${period}.csv`, purchasesCsv(purchases))} className="border-[#242E42]"><DownloadSimple size={16} className="mr-1" /> Exportar CSV</Button>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar compras">
          {PURCHASE_FILTERS.map((filter) => {
            const count = purchases.filter(filter.test).length;
            const active = filter.id === filterId;
            return (
              <button
                key={filter.id}
                type="button"
                aria-pressed={active}
                onClick={() => setFilterId(filter.id)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${active ? 'border-[#E2B05E] bg-[#E2B05E]/15 text-[#E2B05E]' : 'border-[#242E42] text-[#9CA3AF] hover:border-[#3A4560]'}`}
              >
                {filter.label} ({count})
              </button>
            );
          })}
        </div>
        <Table
          columns={['Orden', 'Fecha', 'Proveedor', 'Subtotal', 'ITBMS', 'Respaldo', 'Nombre/RUC de la empresa en la factura', 'Cuenta']}
          empty="No hay compras con este filtro."
          rows={shown.map((row) => {
            const noBuyer = row.reason === 'sin_datos_empresa';
            return (
              <tr key={row.po_number} className={`border-t border-[#242E42] ${noBuyer ? 'bg-red-500/10' : row.included ? '' : 'opacity-70'}`}>
                <td className={cell}>{row.po_number}</td><td className={cell}>{row.date}</td>
                <td className={cell}>{row.supplier_name}{!row.supplier_has_ruc && Number(row.tax) > 0 && <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-300">sin RUC</span>}</td>
                <td className={num}>{formatMoney(row.subtotal)}</td><td className={num}>{formatMoney(row.tax)}</td>
                <td className={cell}>{row.has_support ? 'Sí' : 'No'}</td>
                <td className={cell}>{onBuyerCheck ? <BuyerMarker row={row} disabled={report.frozen} onChange={onBuyerCheck} /> : null}</td>
                <td className={cell}>
                  {row.included
                    ? <span className="text-emerald-400">Crédito</span>
                    : noBuyer
                      ? <span className="rounded bg-red-500/20 px-2 py-0.5 text-xs font-semibold text-red-300" title="Es un gasto de la empresa, pero la factura no está a nombre de la empresa">SIN CRÉDITO · gasto de la empresa</span>
                      : <span className="text-amber-400">{PURCHASE_REASONS[row.reason] || 'No'}</span>}
                </td>
              </tr>
            );
          })}
          footer={(
            <tr><td className={cell} colSpan={3}>Con crédito ({included.length})</td><td className={num}>{formatMoney(sumBy(included, 'subtotal'))}</td><td className={num}>{formatMoney(sumBy(included, 'tax'))}</td><td colSpan={3} /></tr>
          )}
        />
      </TabsContent>

      <TabsContent value="people">
        <PeopleTab report={report} onPayeeType={onPayeeType} />
      </TabsContent>

      <TabsContent value="annex" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-[#9CA3AF]">Detalle del crédito de retención (casilla 52). El objeto de la retención lo define la contadora.</p>
          <Button size="sm" variant="outline" onClick={() => downloadCsv(`anexo95-${period}.csv`, annex95Csv(annex))} className="border-[#242E42]"><DownloadSimple size={16} className="mr-1" /> Exportar CSV</Button>
        </div>
        <Table
          columns={['RUC', 'DV', 'Cliente', 'Factura', 'Monto gravado', 'ITBMS causado', 'ITBMS retenido']}
          empty="No hay facturas con retención en este período."
          rows={annex.map((row) => (
            <tr key={row.document} className="border-t border-[#242E42]">
              <td className={cell}>{row.ruc || <span className="text-red-400">falta</span>}</td><td className={cell}>{row.dv || '—'}</td><td className={cell}>{row.name}</td><td className={cell}>{row.document}</td>
              <td className={num}>{formatMoney(row.taxed_amount)}</td><td className={num}>{formatMoney(row.itbms_caused)}</td><td className={num}>{formatMoney(row.itbms_retained)}</td>
            </tr>
          ))}
          footer={(
            <tr><td className={cell} colSpan={4}>Total</td><td className={num}>{formatMoney(sumBy(annex, 'taxed_amount'))}</td><td className={num}>{formatMoney(sumBy(annex, 'itbms_caused'))}</td><td className={num}>{formatMoney(sumBy(annex, 'itbms_retained'))}</td></tr>
          )}
        />
      </TabsContent>
    </Tabs>
  );
}
